import React, { useMemo, useState } from "react";
import Modal from "react-modal";
import * as XLSX from "xlsx";
import { Icon } from "@iconify/react";
import Select from "react-select";
import { FaInfo, FaInfoCircle } from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";
import { getAllTemplateList } from "services/stockService";

const ImportPopup = ({
  isOpen,
  onClose,
  itemfields,
  skus,
  setExcelResponse,
  categories,
  productTypes,
  designs,
  purities,
  vendorList,
  clientCode,
}) => {
  // State variables
  const { data: TemplateList, refetch: RefetchTemplateList } = useQuery({
    queryKey: [
      "/add_stock_labelled_unlabelled/fetchAllTemplateList",
      clientCode,
    ],
    queryFn: getAllTemplateList,
    refetchOnWindowFocus: false,
    cacheTime: 1000 * 60 * 1,
    staleTime: 1000 * 60 * 1,
  });
  const [headers, setHeaders] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [columnMapping, setColumnMapping] = useState();
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [fileName, setFileName] = useState("");
  const [isFileNameSaved, setIsFileNameSaved] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [showMessage, setShowMessage] = useState(false);

  // Predefined fields for mapping
  const predefinedFields = [
    "TID Value",
    "EPC Value",
    "Category",
    "Product",
    "Purity",
    "Barcode Number",
    "Item Code",
    "Box",
  ];

  // Function to reset the form
  const resetForm = () => {
    setHeaders([]);
    setExcelData([]);
    setColumnMapping({});
    setFileName("");
    setIsFileNameSaved(false);
    setShowTable(false);
    setShowMessage(false);
  };

  // Function to handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onload = (e) => {
        const workbook = XLSX.read(e.target.result, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const parsedData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        console.log(parsedData);
        if (parsedData.length > 0) {
          setHeaders(parsedData[0]);
          setExcelData(parsedData.slice(1));
          // setIsFileNameSaved(false);
          // setShowTable(false);
          // setShowMessage(false);
        }
      };
    }
  };

  // Function to handle column mapping
  const handleMappingChange = (field, selectedOption) => {
    console.log(field);
    console.log(selectedOption);
    setColumnMapping((prev) => ({
      ...prev,
      [field]: selectedOption.value,
    }));
  };

  // Function to save file name
  const handleSaveFileName = () => {
    if (fileName.trim() === "") {
      alert("Please enter a file name before saving.");
      return;
    }
    setIsFileNameSaved(true);
    setShowMessage(true);

    // Hide message after 2 seconds
    setTimeout(() => {
      setShowMessage(false);
    }, 2000);
  };

  // Function to confirm and show table

  const sortedItemFields = useMemo(() => {
    return itemfields
      ?.filter(
        (field) =>
          field?.place === "grid" &&
          field.visibility !== "Hidden" &&
          field.IsVisible !== "Hidden"
      )
      ?.sort((a, b) => a.priority - b.priority);
  }, [itemfields]);


  const handleConfirm = () => {
    const result = excelData?.map((item) => {
      let obj = {};

      sortedItemFields?.map((field) => {
        if (field?.key == "SKUId") {
          const skuvalue = item[headers.indexOf(columnMapping[field?.label])];
          let found = skus?.find((itm) => itm?.StockKeepingUnit == skuvalue);
          obj[field?.key] = columnMapping[field?.label]
            ? { label: skuvalue, value: found?.Id }
            : "";
        } else if (field?.key == "VendorId") {
          const vendorvalue =
            item[headers.indexOf(columnMapping[field?.label])];
          let found = vendorList?.find((itm) => itm?.VendorName == vendorvalue);
          obj[field?.key] = columnMapping[field?.label]
            ? { label: vendorvalue, value: found?.Id }
            : "";
        } else if (field?.key == "CategoryId") {
          const categoryvalue =
            item[headers.indexOf(columnMapping[field?.label])];
          let found = categories?.find(
            (itm) => itm?.CategoryName == categoryvalue
          );
          obj[field?.key] = columnMapping[field?.label]
            ? { label: categoryvalue, value: found?.Id }
            : "";
        } else if (field?.key == "ProductId") {
          const productvalue =
            item[headers.indexOf(columnMapping[field?.label])];
          let found = productTypes?.find(
            (itm) => itm?.ProductName == productvalue
          );
          obj[field?.key] = columnMapping[field?.label]
            ? { label: productvalue, value: found?.Id }
            : "";
        } else if (field?.key == "DesignId") {
          const designvalue =
            item[headers.indexOf(columnMapping[field?.label])];
          let found = designs?.find((itm) => itm?.DesignName == designvalue);
          obj[field?.key] = columnMapping[field?.label]
            ? { label: designvalue, value: found?.Id }
            : "";
        } else if (field?.key == "PurityId") {
          const purityvalue =
            item[headers.indexOf(columnMapping[field?.label])];
          let found = purities?.find((itm) => itm?.PurityName == purityvalue);
          obj[field?.key] = columnMapping[field?.label]
            ? { label: purityvalue, value: found?.Id }
            : "";
        } else {
          obj[field?.key] = columnMapping[field?.label]
            ? item[headers.indexOf(columnMapping[field?.label])]
            : "";
        }
      });
      return obj;
    });
    console.log(result);
    setExcelResponse(result);
    resetForm(); // Reset the form when closing
    onClose();
    // if (!isFileNameSaved) {
    //   alert('Please save the file name first.');
    //   return;
    // }
    // setShowTable(true);
  };


  // Modal animations
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => {
        resetForm();
        onClose();
      }}
      style={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(5px)", // Backdrop blur effect
        },
        content: {
          width: "70%",
          // height: "74vh",
          margin: "80px auto",
          padding: "16px",
          borderRadius: "10px",
          position: "relative",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <button
        onClick={() => {
          resetForm();
          onClose();
        }}
        style={{
          position: "absolute",
          top: "0",
          right: "-6px",
          border: "none",
          background: "transparent",
          cursor: "pointer",
        }}
      >
        <Icon icon="mdi:close" style={{ fontSize: "24px", color: "black" }} />
      </button>

      {showTable ? (
        <>
          <h3 style={{ textAlign: "center", marginBottom: "20px" }}>
            Excel Data
          </h3>
          <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
            <table
              border="1"
              cellPadding="5"
              cellSpacing="0"
              style={{ width: "100%" }}
            >
              <thead>
                <tr>
                  {sortedItemFields?.map((field) => (
                    <th key={field?.key}>{field?.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {excelData.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {sortedItemFields.map((field, cellIndex) => (
                      <td key={cellIndex}>
                        {columnMapping[field?.label]
                          ? row[headers.indexOf(columnMapping[field?.label])]
                          : ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          {/* <h2
            style={{
              textAlign: "center",
              marginBottom: "20px",
              marginTop: "20px",
            }}
          >
            Select Excel Headingss
          </h2> */}

          <div
            style={{
              display: "flex",
              gap: "10px",
              marginBottom: "10px",
              alignItems: "center",
              marginTop: '10px'
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                alignItems: "start",
              }}
            >
              <label
                htmlFor="file-upload"
                style={{ marginBottom: "5px", fontWeight: "bold" }}
              >
                Upload File:
              </label>
              <input
                id="file-upload"
                type="file"
                accept=".xlsx, .xls"
                onChange={handleFileUpload}
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                alignItems: "start",
              }}
            >
              <label
                htmlFor="dropdown-select"
                style={{ marginBottom: "5px", fontWeight: "bold" }}
              >
                Select Template:
              </label>
              <select
                onChange={(e) => {
                  console.log(e.target.value);
                  setSelectedTemplate({
                    Vendor: "Vendor",
                    SKU: "SKU",
                    RFIDCode: "RFID CODE",
                    TidValue: "TID VALUE",
                    TotalWT: "TOTAL WEIGHT",
                    "Packing Weight": "PACKING WEIGHT",
                    Category: "Category",
                    Product: "Product",
                    Purity: "Purity",
                    Design: "Design",
                  });
                }}
                id="dropdown-select"
                style={{
                  padding: "5px",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  height: "40px",
                }}
              >
                <option>-- select --</option>
                {TemplateList?.length
                  ? TemplateList?.map((item) => (
                      <option
                        value={{
                          Vendor: "Vendor",
                          SKU: "SKU",
                          RFIDCode: "RFID CODE",
                          TidValue: "TID VALUE",
                          TotalWT: "TOTAL WEIGHT",
                          "Packing Weight": "PACKING WEIGHT",
                          Category: "Category",
                          Product: "Product",
                          Purity: "Purity",
                          Design: "Design",
                        }}
                      >
                        {item?.label}
                      </option>
                    ))
                  : []}
              </select>
            </div>
          </div>

          {/* Scrollable Column Mapping Section */}
          {sortedItemFields?.length > 0 && (
            <div
              style={{
                flex: 1,
                overflow: "scroll",
                overflowY: "auto",
                scrollbarWidth: 'thin',
                maxHeight: "40vh",
                paddingBottom: "20px",
              }}
            >
              <div
              // style={{
              //   display: 'grid',
              //   gridTemplateColumns: 'repeat(4, 1fr)',
              //   gap: '10px',
              //   padding: '10px'
              // }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "10px",

                    padding: "10px",
                    borderRadius: "5px",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      // textAlign: "left",
                      fontSize: "14px",
                      border: "1px solid #ccc",
                      width: "fit-content",
                      border: "none",
                      padding: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    Predefined Labels
                  </span>
                  <span
                    style={{
                      display: "flex",
                      // textAlign: "left",
                      fontSize: "14px",
                      border: "1px solid #ccc",
                      width: "fit-content",
                      border: "none",
                      padding: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    Select Excel Labels
                  </span>
                </div>


                {sortedItemFields?.map((field) => {
                  return (
                    <>
                      {field?.label != "Stones" && field?.label != "Images" && (
                        <>
                          <div
                            key={field?.key} 
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: "10px",
                              border: "1px doted #ccc",
                              borderStyle: "dashed",
                              borderWidth: "1px",
                              borderColor: "#ccc",
                              padding: "10px",
                              borderRadius: "5px",
                            }}
                          >
                            <span
                              style={{
                                display: "flex",
                                textAlign: "left",
                                fontSize: "12px",
                                border: "1px solid #ccc",
                                width: "40%",
                                borderRadius: "10px",
                                backgroundColor: "#f8f8f8",
                                padding: "10px",
                              }}
                            >
                              {field?.label}
                            </span>
                            <div style={{width: '40%'}}>
                            <Select
                            
                              options={headers.map((header) => ({
                                value: header,
                                label: header,
                              }))}
                              menuPortalTarget={document.body}
                             
                              onChange={(selectedOption) => {
                                handleMappingChange(
                                  field?.label,
                                  selectedOption
                                );
                              }}
                              isSearchable={true}
                              styles={{
                                control: (base) => ({
                                  ...base,
                             
                                  menu: (base) => ({
                                    ...base,
                                    zIndex: 9999, // 👈 ensure this is higher than the modal's z-index
                                  }),
                                 
                                }),
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999, // 👈 ensure it's above the modal
                                }),
                              }}
                              // isClearable={true}
                            />
                            </div>
                            
                          </div>
                        </>
                      )}
                    </>
                  );
                })}
              </div>
            </div>
          )}

          {/* Save File Name & Confirm Section */}
          {/* {headers.length > 0 && ( */}
          <div
            style={{
              paddingTop: "10px",
              borderTop: "1px solid #ddd",
              background: "white",
              // paddingBottom: "10px",
            }}
          >
            {/* <p
              style={{
                fontWeight: "bold",
                marginBottom: "10px",
                textAlign: "left",
              }}
            >
              Template Name
            </p> */}

            <div
              style={{
                display: "flex",
                gap: "10px",
                marginBottom: "10px",
                alignItems: "center",
                // width: "75%",
              }}
            >
              <label htmlFor="" style={{fontWeight: 'bold', fontSize: '14px'}}> Template Name</label>
              <input
                type="text"
                placeholder="Enter template name"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                style={{
                  flex: 2,
                  padding: "8px",
                  borderRadius: "5px",
                  border: "1px solid #0077d4",
                  fontSize: "16px",
                }}
              />

              <div style={{marginLeft: '10px', display: 'flex', gap: '10px', width: '40%'}}>
              <button
                onClick={handleSaveFileName}
                style={{
                  // flex: 1,
                  padding: "10px",
                  backgroundColor: "#0077d4",
                  color: "white",
                  width: "30%",
                }}
              >
                <Icon icon="ic:outline-check" width="24" height="24" />
                Save
              </button>

              {/* Confirm Button  */}

              <button
                onClick={handleConfirm}
                style={{
                  // flex: 1,
                  padding: "10px",
                  backgroundColor: "#0077d4",
                  color: "white",
                  width: "30%",
                }}
              >
                <Icon icon="ic:outline-check" width="24" height="24" />
                Confirm
              </button>
              </div>

          

            </div>
            <p
              style={{
                color: "gray",
                fontStyle: "italic",
                textAlign: "left",
                padding: "0px",
                margin: "0px",
                fontSize: "12px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <FaInfoCircle /> Save this template for future use and easy
              access.
            </p>

            {/* Small message for successful save */}
            {showMessage && (
              <p
                style={{
                  color: "green",
                  fontSize: "14px",
                  marginTop: "-10px",
                }}
              >
                ✅ File name saved!
              </p>
            )}
          </div>
          {/* )} */}
        </>
      )}
    </Modal>
  );
};

export default ImportPopup;
